import React, { createContext, useContext, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AlertContext = createContext();

export const AlertProvider = ({ children }) => {
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showSuccess = (message, duration = 3000) => {
    toast({
      variant: 'success',
      title: 'Success',
      description: message,
      duration,
    });
  };

  const showError = (message, duration = 7000) => {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: message,
      duration,
    });
  };

  const showWarning = (message, duration = 5000) => {
    toast({
      variant: 'warning',
      title: 'Warning',
      description: message,
      duration,
    });
  };

  const showInfo = (message, duration = 3000) => {
    toast({
      variant: 'info',
      title: 'Info',
      description: message,
      duration,
    });
  };

  const confirm = (title, message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        title,
        message,
        variant: options.variant || 'default',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        destructive: options.destructive || false,
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        },
      });
    });
  };

  return (
    <AlertContext.Provider
      value={{ showSuccess, showError, showWarning, showInfo, confirm }}
    >
      {children}
      {confirmDialog && (
        <AlertDialog open={true} onOpenChange={(open) => !open && confirmDialog.onCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDialog.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={confirmDialog.onCancel}>
                {confirmDialog.cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDialog.onConfirm}
                className={
                  confirmDialog.destructive
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                    : ''
                }
              >
                {confirmDialog.confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
